import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaTimes,
  FaFacebookF, 
  FaEnvelope, 
  FaCopy,
  FaCheck
} from 'react-icons/fa';
import {FaXTwitter} from 'react-icons/fa6';
import { Icon } from '../common/Icon';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
  description?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  url,
  title,
  description
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Ensure we have a full URL
  const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  
  // Encode for sharing
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || title);

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
    window.open(twitterUrl, '_blank', 'width=550,height=450');
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    window.open(facebookUrl, '_blank', 'width=550,height=450');
  };

  const handleEmailShare = () => {
    const subject = encodedTitle;
    const body = `${encodedDescription}%0A%0A${encodedUrl}`;
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Share Meditation</ModalTitle>
          <CloseButton onClick={onClose}>
            <Icon icon={FaTimes} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ShareGrid>
            <ShareButton 
              onClick={handleTwitterShare}
              $platform="twitter"
              title="Share on X (Twitter)"
            >
              <IconWrapper>
                <Icon icon={FaXTwitter} size={24} />
              </IconWrapper>
              <ShareLabel>X (Twitter)</ShareLabel>
            </ShareButton>

            <ShareButton 
              onClick={handleFacebookShare}
              $platform="facebook"
              title="Share on Facebook"
            >
              <IconWrapper>
                <Icon icon={FaFacebookF} size={24} />
              </IconWrapper>
              <ShareLabel>Facebook</ShareLabel>
            </ShareButton>

            <ShareButton 
              onClick={handleEmailShare}
              $platform="email"
              title="Share via Email"
            >
              <IconWrapper>
                <Icon icon={FaEnvelope} size={24} />
              </IconWrapper>
              <ShareLabel>Email</ShareLabel>
            </ShareButton>

            <ShareButton 
              onClick={handleCopyToClipboard}
              $platform="copy"
              title="Copy link to clipboard"
            >
              <IconWrapper>
                <Icon icon={copied ? FaCheck : FaCopy} size={24} />
              </IconWrapper>
              <ShareLabel>{copied ? 'Copied!' : 'Copy Link'}</ShareLabel>
            </ShareButton>
          </ShareGrid>

          <UrlPreview>
            <UrlLabel>Share URL:</UrlLabel>
            <UrlText>{fullUrl}</UrlText>
          </UrlPreview>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #333;
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ShareGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ShareButton = styled.button<{ $platform: string }>`
  background: transparent;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 16px 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  color: ${props => {
    switch(props.$platform) {
      case 'twitter': return '#1DA1F2';
      case 'facebook': return '#1877F2';
      case 'email': return '#EA4335';
      case 'copy': return '#4CAF50';
      default: return '#888';
    }
  }};

  &:hover {
    background: ${props => {
      switch(props.$platform) {
        case 'twitter': return 'rgba(29, 161, 242, 0.1)';
        case 'facebook': return 'rgba(24, 119, 242, 0.1)';
        case 'email': return 'rgba(234, 67, 53, 0.1)';
        case 'copy': return 'rgba(76, 175, 80, 0.1)';
        default: return 'rgba(255, 255, 255, 0.1)';
      }
    }};
    border-color: ${props => {
      switch(props.$platform) {
        case 'twitter': return '#1DA1F2';
        case 'facebook': return '#1877F2';
        case 'email': return '#EA4335';
        case 'copy': return '#4CAF50';
        default: return '#666';
      }
    }};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`;

const ShareLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
`;

const UrlPreview = styled.div`
  background: #0d0d0d;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 12px;
`;

const UrlLabel = styled.div`
  color: #888;
  font-size: 12px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UrlText = styled.div`
  color: #ccc;
  font-size: 14px;
  word-break: break-all;
  font-family: 'SF Mono', Monaco, monospace;
`;

export default ShareModal;